import React from 'react';
import { Cloud, Shield, Key, Eye, Play, CheckCircle, XCircle, Square, CheckSquare, Zap } from 'lucide-react';
import { Incident } from './Dashboard';

interface ThreatFeedProps {
  onSelectIncident: (incident: Incident) => void;
  demoMode?: boolean;
  autoRemediate?: boolean;
  onStatsChange?: (stats: { active: number; remediated: number; total: number }) => void;
  refreshKey?: number;
  incidents: Incident[];
  setIncidents: React.Dispatch<React.SetStateAction<Incident[]>>;
  selectedIds: Set<string>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  sourceFilter: 'all' | 'aws' | 'crowdstrike' | 'okta';
  setSourceFilter: React.Dispatch<React.SetStateAction<'all' | 'aws' | 'crowdstrike' | 'okta'>>;
  severityFilter: 'all' | 'critical' | 'high' | 'medium' | 'low';
  setSeverityFilter: React.Dispatch<React.SetStateAction<'all' | 'critical' | 'high' | 'medium' | 'low'>>;
  onAnalyze?: (incident: Incident) => void;
  onAutoRemediate?: (incident: Incident) => void;
}

const ThreatFeed: React.FC<ThreatFeedProps> = ({ 
  onSelectIncident, 
  demoMode = true, 
  autoRemediate = false,
  onStatsChange,
  incidents,
  setIncidents,
  selectedIds,
  setSelectedIds,
  sourceFilter,
  setSourceFilter,
  severityFilter,
  setSeverityFilter,
  onAnalyze,
  onAutoRemediate
}) => {

  const filteredIncidents = incidents
    .filter(i => sourceFilter === 'all' || i.source === sourceFilter)
    .filter(i => severityFilter === 'all' || i.severity === severityFilter);

  const allFilteredSelected = filteredIncidents.length > 0 && 
    filteredIncidents.every(i => selectedIds.has(i.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredIncidents.forEach(i => next.delete(i.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredIncidents.forEach(i => next.add(i.id));
        return next;
      });
    }
  };

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getSourceIcon = (source: Incident['source']) => {
    switch (source) {
      case 'aws': return <Cloud className="w-4 h-4" />;
      case 'crowdstrike': return <Shield className="w-4 h-4" />;
      case 'okta': return <Key className="w-4 h-4" />;
    }
  };

  const getSourceColor = () => 'text-white bg-transparent border-white/30';

  const getSeverityBadge = (severity: Incident['severity']) => {
    const classes = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/50',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      low: 'bg-green-500/20 text-green-400 border-green-500/50',
    };
    return classes[severity];
  };

  const getStatusDisplay = (incident: Incident) => {
    if (incident.status === 'remediated') {
      return (
        <span className="flex items-center gap-1 text-sm text-green-400">
          <CheckCircle className="w-3 h-3" /> Remediated
        </span>
      );
    }
    
    return (
      <span className="flex items-center gap-1 text-sm text-red-400">
        <XCircle className="w-3 h-3" /> Active
      </span>
    );
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  const handleAnalyze = (e: React.MouseEvent, incident: Incident) => {
    e.stopPropagation();
    if (onAnalyze) {
      onAnalyze(incident);
    } else {
      onSelectIncident(incident);
    }
  };

  const handleAutoRemediate = (e: React.MouseEvent, incident: Incident) => {
    e.stopPropagation();
    if (onAutoRemediate) {
      onAutoRemediate(incident);
    } else {
      // Default behavior: select incident with auto-remediate flag
      onSelectIncident({ ...incident, status: 'analyzing' });
    }
  };

  return (
    <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-arbitium-primary/10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-arbitium-accent" />
            Threat Feed
            <span className="ml-2 text-sm text-gray-400">({incidents.length})</span>
          </h2>
          {/* Select All */}
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            {allFilteredSelected ? (
              <CheckSquare className="w-4 h-4 text-arbitium-accent" />
            ) : (
              <Square className="w-4 h-4" />
            )}
            {allFilteredSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {/* Source Filter tabs */}
        <div className="flex gap-2 mb-3">
          {(['all', 'aws', 'crowdstrike', 'okta'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setSourceFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                sourceFilter === f 
                  ? 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50' 
                  : 'text-gray-400 hover:text-white hover:bg-arbitium-primary/10'
              }`}
            >
              {f === 'all' ? 'All Sources' : f === 'aws' ? 'AWS' : f === 'crowdstrike' ? 'CrowdStrike' : 'Okta'}
            </button>
          ))}
        </div>
        
        {/* Severity Filter tabs */}
        <div className="flex gap-2">
          {(['all', 'critical', 'high', 'medium', 'low'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setSeverityFilter(s)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                severityFilter === s 
                  ? s === 'critical' ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                  : s === 'high' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                  : s === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                  : s === 'low' ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50'
                  : 'text-gray-400 hover:text-white hover:bg-arbitium-primary/10'
              }`}
            >
              {s === 'all' ? 'All Severities' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Incident list */}
      <div className="max-h-[600px] overflow-y-auto">
        {filteredIncidents.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No threats detected
          </div>
        ) : (
          filteredIncidents.map((incident) => (
            <div
              key={incident.id}
              className={`p-4 border-b border-arbitium-primary/10 hover:bg-arbitium-primary/5 transition-colors ${
                incident.status === 'remediated' ? 'opacity-60' : ''
              } ${selectedIds.has(incident.id) ? 'bg-arbitium-accent/20 border-l-4 border-l-arbitium-accent' : ''}`}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <button
                  onClick={(e) => toggleSelect(incident.id, e)}
                  className="mt-1 text-gray-400 hover:text-white transition-colors"
                >
                  {selectedIds.has(incident.id) ? (
                    <CheckSquare className="w-5 h-5 text-arbitium-accent" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>

                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onSelectIncident(incident)}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-xs rounded border flex items-center gap-1 ${getSourceColor()}`}>
                      {getSourceIcon(incident.source)}
                      {incident.source.toUpperCase()}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded border ${getSeverityBadge(incident.severity)}`}>
                      {incident.severity.toUpperCase()}
                    </span>
                  </div>
                  <h3 className="font-medium text-white truncate">{incident.title}</h3>
                  <p className="text-sm text-gray-400 truncate">{incident.description}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {getStatusDisplay(incident)}
                  <span className="text-xs text-gray-500">{formatTime(incident.timestamp)}</span>
                  {/* Action Buttons - Always show both for active threats */}
                  {incident.status !== 'remediated' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => handleAnalyze(e, incident)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-arbitium-primary/20 text-arbitium-accent border border-arbitium-primary/30 rounded-lg hover:bg-arbitium-primary/30 transition-colors"
                      >
                        <Play className="w-3 h-3" />
                        Analyze
                      </button>
                      <button
                        onClick={(e) => handleAutoRemediate(e, incident)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border border-green-500/30 rounded-lg hover:from-green-500/30 hover:to-emerald-500/30 transition-colors"
                      >
                        <Zap className="w-3 h-3" />
                        Auto-Remediate
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ThreatFeed;
